import type { ReactNode } from 'react';

type Tone = 'neutral' | 'accent' | 'pos' | 'neg' | 'warning' | 'muted';
type Size = 'sm' | 'md';

interface Props {
  children: ReactNode;
  tone?: Tone;
  size?: Size;
  /** Render as a button with hover/active affordances. */
  onClick?: () => void;
  /** Visually pressed state when used as a button. */
  active?: boolean;
  /** Optional leading dot (e.g., color swatch). */
  dotColor?: string;
  leading?: ReactNode;
  trailing?: ReactNode;
  title?: string;
}

const TONE_STATIC: Record<Tone, string> = {
  neutral: 'border-ink-200 bg-white text-ink-900 dark:border-ink-700 dark:bg-ink-800 dark:text-ink-50',
  accent:
    'border-accent-500/40 bg-accent-50 text-accent-700 dark:border-accent-500/50 dark:bg-accent-700/20 dark:text-accent-500',
  pos:
    'border-pos-500/40 bg-pos-50 text-pos-700 dark:border-pos-500/40 dark:bg-pos-700/20 dark:text-pos-500',
  neg:
    'border-neg-500/40 bg-neg-50 text-neg-700 dark:border-neg-500/40 dark:bg-neg-700/20 dark:text-neg-500',
  warning:
    'border-amber-500/40 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-700/20 dark:text-amber-400',
  muted:
    'border-ink-100 bg-ink-50 text-ink-400 dark:border-ink-800 dark:bg-ink-900 dark:text-ink-300',
};

const SIZE: Record<Size, string> = {
  sm: 'px-2 py-0.5 text-[11px] gap-1',
  md: 'px-2.5 py-1 text-xs gap-1.5',
};

export function Chip({
  children,
  tone = 'neutral',
  size = 'md',
  onClick,
  active,
  dotColor,
  leading,
  trailing,
  title,
}: Props) {
  const base = `inline-flex items-center rounded-full border font-medium transition ${SIZE[size]}`;
  const toneCls = active && onClick ? TONE_STATIC.neutral : TONE_STATIC[tone];
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title}
        aria-pressed={active}
        className={`${base} ${toneCls} hover:shadow-sm`}
      >
        {dotColor && (
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: dotColor }}
          />
        )}
        {leading}
        {children}
        {trailing}
      </button>
    );
  }
  return (
    <span className={`${base} ${toneCls}`} title={title}>
      {dotColor && (
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: dotColor }}
        />
      )}
      {leading}
      {children}
      {trailing}
    </span>
  );
}
